import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Lightbulb, ShieldCheck, Handshake, ArrowRight, Download } from "lucide-react";
import useReveal from "@/hooks/useReveal";
import ContactForm from "@/components/site/ContactForm";
import BrochureDialog from "@/components/site/BrochureDialog";

const QUESTIONS = [
  {
    q: "When a pipe bursts at home and no adult is around — does a 12-year-old know where the main water valve is?",
    f: "Most don't. By then, a single unshut valve can flood a flat in under ten minutes.",
  },
  {
    q: "If a stove fire flares up from hot oil — would a child throw water on it?",
    f: "Most would. Water on an oil fire creates an explosion of flame. The correct action — sliding a lid over it — is almost never taught.",
  },
  {
    q: "Does the average teenager know that scanning a QR code never receives money — it only sends it?",
    f: "Most don't. This single gap is how thousands lose money on resale apps every month.",
  },
  {
    q: "Could a 14-year-old get a government certificate themselves — for about ₹30 online instead of ₹800 through an agent?",
    f: "Almost none know the portal exists. Families across India overpay agents for services that are nearly free.",
  },
  {
    q: "Does anyone leaving school know the one fact that defeats a 'digital arrest' phone call?",
    f: "A scam that took ₹45 lakh from a retired IAS officer and ₹56 lakh from an Army Colonel in 2025. The defence is one fact, known in advance: no agency ever arrests anyone by video call.",
  },
  {
    q: "Can a 16-year-old explain why ₹500 a month invested at 17 becomes about ₹78 lakh by 60 — but only ₹24 lakh if they start at 27?",
    f: "That ₹54 lakh difference is just ten years of delay. Compound interest is the most powerful idea in personal finance, and it is on no school syllabus.",
  },
  {
    q: "Would a young adult know what 'normal wear and tear' means when a landlord refuses to return their deposit?",
    f: "Most lose the deposit by default. The law is clearly on the tenant's side — but only for those who know it.",
  },
  {
    q: "Does a teenager know to pause for six seconds before reacting in anger — and why those six seconds change the outcome?",
    f: "It is basic neuroscience. The emotional brain overrides the rational one for about six seconds. Almost no one is taught this.",
  },
];

const BELIEFS = [
  { Icon: Lightbulb, text: "Aware enough to decide for themselves." },
  { Icon: ShieldCheck, text: "Aware enough never to be misled or overcharged." },
  { Icon: Handshake, text: "Aware enough to get real value from any expert — and judge what's fair to pay." },
];

const LEVELS = [
  {
    href: "/level-1",
    accent: "var(--teal)",
    badge: "Level 1 — Home Intelligence",
    age: "Age 10–12",
    body: "Can a child handle a home emergency alone — electrical, water, gas, fire, first aid — without panic and without waiting for an adult? By the end of Level 1, they can. 24 chapters on running and protecting a home independently.",
  },
  {
    href: "/level-2",
    accent: "var(--teal)",
    badge: "Level 2 — System Intelligence",
    age: "Age 12–14",
    body: "Can a teenager deal with a bank, a government office, a landlord, or a fraud attempt without being cheated or sent to a middleman? By the end of Level 2, they can navigate the outside world on their own. 24 chapters.",
  },
  {
    href: "/level-3",
    accent: "var(--gold)",
    badge: "Level 3 — Life Intelligence",
    age: "Age 14–16",
    body: "Can a young adult manage emotions under pressure, invest their first income wisely, lead a team, and design a life around what they actually value? By the end of Level 3, they have the framework to. 24 chapters.",
  },
];

const STEPS = [
  { n: "01", t: "Knowledge", d: "The facts, anchored in a real story." },
  { n: "02", t: "Practice", d: "10 to 15 real situations role-played, with the exact words said aloud." },
  { n: "03", t: "Reflex", d: "The right action fires automatically when the real moment comes." },
];

export default function Home() {
  useReveal();
  const [brochureOpen, setBrochureOpen] = useState(false);
  const [guideOpen, setGuideOpen] = useState(false);
  useEffect(() => { document.title = "LifeIQ — Because marks alone won't run life."; }, []);

  return (
    <main>
      {/* SECTION 1 — HERO */}
      <section
        className="bg-[#0D1B2A] text-white relative"
        style={{
          backgroundImage:
            "linear-gradient(rgba(13,27,42,0.93) 0%, rgba(13,27,42,0.96) 100%), url('https://images.unsplash.com/photo-1503676260728-1c00da094a0b?auto=format&fit=crop&w=1920&q=60')",
          backgroundSize: "cover",
          backgroundPosition: "center right",
        }}
      >
        <div className="max-w-[1240px] mx-auto px-5 sm:px-8 pt-20 md:pt-28 pb-24 md:pb-32">
          <div className="max-w-4xl reveal">
            <div className="text-[12px] uppercase tracking-[0.22em] text-[#F57F17] font-semibold">LIFEIQ · Real Life Skills Learning</div>
            <p className="mt-4 font-serif-d text-[18px] sm:text-[20px] lg:text-[22px] text-[#F57F17] leading-[1.4] max-w-2xl font-medium">
              A Real-World Intelligence System that reduces dependency and increases informed decision-making.
            </p>
            <h1 className="mt-7 font-serif-d text-[44px] sm:text-6xl lg:text-[76px] leading-[1.02] tracking-tight text-white">
              Because marks alone<br className="hidden sm:block" /> won't run life.
            </h1>
            <p className="mt-8 text-[17px] sm:text-[19px] text-white/85 leading-[1.7] max-w-3xl">
              A 16-year-old today can solve a quadratic equation. But can they turn off the water main when a pipe bursts, recognise a fraud that has cheated retired IAS officers, or explain why ₹500 saved at 17 beats ₹500 saved at 27? LifeIQ teaches students aged 10–16 the things life will actually demand of them.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-3">
              <a href="#contact" data-testid="hero-book-pilot" className="inline-flex items-center justify-center gap-2 bg-[#F57F17] text-white px-7 py-4 text-[13px] font-semibold tracking-wide uppercase hover:brightness-95 transition rounded-sm">
                Book a Pilot Conversation <ArrowRight size={15} />
              </a>
              <button
                type="button"
                onClick={() => setBrochureOpen(true)}
                data-testid="hero-brochure"
                className="inline-flex items-center justify-center gap-2 bg-transparent text-white border border-white/40 px-7 py-4 text-[13px] font-semibold tracking-wide uppercase hover:bg-white/10 transition rounded-sm"
              >
                <Download size={14} /> Download Brochure
              </button>
            </div>
          </div>

          <div className="mt-16 pt-7 border-t border-white/15 reveal">
            <div className="flex flex-wrap items-center gap-x-10 gap-y-3 text-[13px] sm:text-[14px] text-white/80">
              <span><span className="text-[#F57F17] font-semibold">3</span> Levels</span>
              <span className="h-3 w-px bg-white/20 hidden sm:inline-block" />
              <span><span className="text-[#F57F17] font-semibold">72</span> Chapters</span>
              <span className="h-3 w-px bg-white/20 hidden sm:inline-block" />
              <span><span className="text-[#F57F17] font-semibold">1,080+</span> real-life situations</span>
              <span className="h-3 w-px bg-white/20 hidden sm:inline-block" />
              <span>Aligned with NEP 2020</span>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 2 — DID YOU KNOW THIS */}
      <section id="program" className="bg-white">
        <div className="max-w-[1240px] mx-auto px-5 sm:px-8 py-24 md:py-32">
          <div className="max-w-3xl reveal">
            <div className="text-[12px] uppercase tracking-[0.22em] text-[#004D40] font-semibold">The honest test</div>
            <h2 className="mt-5 font-serif-d text-[36px] sm:text-5xl lg:text-[58px] leading-[1.05]">
              How much of this does a school-leaver actually know?
            </h2>
            <p className="mt-6 text-[17px] sm:text-[19px] text-[#5C6470] max-w-2xl">
              Read these slowly. Notice the ones that make you pause.
            </p>
          </div>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
            {QUESTIONS.map((c, i) => (
              <article
                key={i}
                data-testid={`question-card-${i}`}
                className="reveal pl-6 sm:pl-7 py-2 border-l-[3px] border-[#F57F17]"
                style={{ transitionDelay: `${(i % 2) * 80}ms` }}
              >
                <h3 className="font-serif-d text-[22px] sm:text-[26px] leading-[1.25] text-[#0D1B2A]">
                  {c.q}
                </h3>
                <p className="mt-4 text-[16px] text-[#5C6470] leading-[1.65] max-w-xl">{c.f}</p>
              </article>
            ))}
          </div>

          <div className="mt-20 max-w-3xl reveal">
            <p className="font-serif-d text-[26px] sm:text-[34px] leading-[1.25] text-[#F57F17]">
              If even a few of these gave you pause — that is the gap. And right now, almost nothing in school is built to close it.
            </p>
          </div>
        </div>
      </section>

      {/* SECTION 3 — CORE BELIEF */}
      <section className="bg-[#F5F5F4]">
        <div className="max-w-[1240px] mx-auto px-5 sm:px-8 py-24 md:py-32">
          <div className="max-w-3xl reveal">
            <div className="text-[12px] uppercase tracking-[0.22em] text-[#004D40] font-semibold">Why LifeIQ exists</div>
            <h2 className="mt-5 font-serif-d text-[36px] sm:text-5xl lg:text-[56px] leading-[1.06]">
              You don't need to be an expert in everything. You only need to be aware enough to decide well.
            </h2>
          </div>

          <div className="mt-12 max-w-3xl space-y-7 text-[17px] sm:text-[19px] text-[#1A1A1A] leading-[1.75] reveal">
            <p>
              No one can master every field — money, law, health, technology, home repair. Nobody should try. Life is too wide for that, and real experts will always have their place.
            </p>
            <p>
              But there is a world of difference between being an expert and being aware. The aware person knows enough to ask the right question, to sense when something is wrong, and to make a sound decision. And when they do turn to an expert or a service provider, awareness is exactly what lets them get real value — the right work, done well, at a fair price. They aren't trying to replace the specialist. They simply can't be misled, short-changed, or overcharged.
            </p>
          </div>

          <div className="mt-16 max-w-4xl mx-auto text-center reveal">
            <p className="font-serif-d italic text-[26px] sm:text-[34px] lg:text-[40px] leading-[1.25] text-[#F57F17]">
              "Awareness isn't about doing everything yourself. It's about getting true value from those who help you — and never being at the mercy of someone who knows more than you."
            </p>
          </div>

          <div className="mt-16 max-w-3xl text-[17px] sm:text-[19px] text-[#1A1A1A] leading-[1.75] reveal">
            <p>
              That is the whole idea behind LifeIQ. We don't try to turn children into experts in twenty fields. We make them aware across the areas life will actually test them — so that whether they handle something themselves or hire a professional, the decision is theirs, made with open eyes, and the money they spend brings real value back.
            </p>
          </div>

          <div className="mt-16 max-w-4xl mx-auto text-center reveal">
            <p className="font-serif-d text-[24px] sm:text-[30px] lg:text-[34px] leading-[1.3] text-[#0D1B2A]">
              The larger the gap between a person's awareness and the challenge they face, the greater the potential loss.
              <span className="block mt-3 text-[#F57F17]">LifeIQ exists to reduce that gap.</span>
            </p>
          </div>

          <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {BELIEFS.map(({ Icon, text }, i) => (
              <div key={i} className="reveal flex items-start gap-4 bg-white border border-[#E5E7EA] p-7 rounded-sm" style={{ transitionDelay: `${i * 80}ms` }}>
                <div className="w-11 h-11 rounded-full bg-[#004D40] text-white flex items-center justify-center shrink-0">
                  <Icon size={20} />
                </div>
                <div className="font-serif-d text-[18px] sm:text-[19px] leading-snug text-[#0D1B2A]">{text}</div>
              </div>
            ))}
          </div>

          <div className="mt-12 reveal">
            <Link
              to="/philosophy"
              data-testid="home-philosophy-link"
              className="inline-flex items-center gap-2 text-[#004D40] text-[15px] sm:text-[16px] font-semibold border-b border-[#004D40]/40 hover:border-[#004D40] pb-0.5 transition-colors"
            >
              Read the full LifeIQ philosophy →
            </Link>
          </div>
        </div>
      </section>

      {/* SECTION 4 — THREE LEVELS */}
      <section id="levels" className="bg-white">
        <div className="max-w-[1240px] mx-auto px-5 sm:px-8 py-24 md:py-32">
          <div className="max-w-3xl reveal">
            <div className="text-[12px] uppercase tracking-[0.22em] text-[#004D40] font-semibold">The curriculum</div>
            <h2 className="mt-5 font-serif-d text-[36px] sm:text-5xl lg:text-[56px] leading-[1.06]">
              One curriculum. Three identities. Six years of quietly becoming capable.
            </h2>
            <p className="mt-6 text-[17px] sm:text-[19px] text-[#5C6470] max-w-2xl">
              LifeIQ is not a set of tips. It is a deliberate progression — each level builds a specific capability a young person carries into adulthood.
            </p>
          </div>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {LEVELS.map((l, i) => (
              <Link
                key={i}
                to={l.href}
                data-testid={`level-card-${i}`}
                className="reveal bg-white border border-[#E5E7EA] rounded-sm overflow-hidden flex flex-col hover:shadow-[0_8px_30px_rgba(13,27,42,0.07)] hover:-translate-y-0.5 transition group"
                style={{ transitionDelay: `${i * 90}ms` }}
              >
                <div className="h-1.5" style={{ backgroundColor: l.accent }} />
                <div className="p-7 sm:p-8 flex flex-col gap-5 grow">
                  <div>
                    <div className="text-[12px] uppercase tracking-[0.22em] font-semibold" style={{ color: l.accent }}>{l.badge}</div>
                    <div className="mt-2 text-[14px] text-[#5C6470]">{l.age}</div>
                  </div>
                  <p className="text-[16px] text-[#1A1A1A] leading-[1.7]">{l.body}</p>
                  <div className="mt-auto pt-2 text-[12px] uppercase tracking-[0.18em] font-semibold flex items-center gap-2" style={{ color: l.accent }}>
                    Explore this level
                    <span className="transition-transform group-hover:translate-x-0.5">→</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>

          <div className="mt-14 reveal flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 border-t border-[#E5E7EA] pt-10">
            <div className="max-w-xl">
              <div className="font-serif-d text-[22px] sm:text-[26px] text-[#0D1B2A] leading-snug">
                Want the full programme on paper?
              </div>
              <p className="mt-2 text-[15px] sm:text-[16px] text-[#5C6470] leading-[1.7]">
                The complete LifeIQ programme guide — all 72 chapters, the cyber-safety module, classroom rhythm and outcomes — in one PDF.
              </p>
            </div>
            <button
              type="button"
              onClick={() => setGuideOpen(true)}
              data-testid="levels-guide"
              className="inline-flex items-center gap-2 bg-[#0D1B2A] text-white px-7 py-4 rounded-sm text-[13px] font-semibold uppercase tracking-wide hover:bg-[#004D40] transition shrink-0"
            >
              <Download size={14} /> Download Complete Program Guide
            </button>
          </div>
        </div>
      </section>

      {/* SECTION 5 — HOW IT WORKS */}
      <section className="bg-[#F5F5F4]">
        <div className="max-w-[1240px] mx-auto px-5 sm:px-8 py-24 md:py-32">
          <div className="max-w-3xl reveal">
            <div className="text-[12px] uppercase tracking-[0.22em] text-[#004D40] font-semibold">How it works</div>
            <h2 className="mt-5 font-serif-d text-[36px] sm:text-5xl lg:text-[56px] leading-[1.06]">
              Knowing is not enough. We build reflexes.
            </h2>
          </div>

          <p className="mt-8 max-w-3xl text-[17px] sm:text-[19px] text-[#1A1A1A] leading-[1.75] reveal">
            A student can read every word about fraud, score full marks on a test, and still scan a scammer's QR code the next week — because knowledge in memory does not become action under pressure. Only practice does. Every LifeIQ chapter moves learning from the page into automatic response, the way a fire drill works better than a fire lecture.
          </p>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-8">
            {STEPS.map((s, i) => (
              <div key={s.n} className="reveal" style={{ transitionDelay: `${i * 90}ms` }}>
                <div className="font-serif-d text-[42px] text-[#F57F17] leading-none">{s.n}</div>
                <div className="mt-4 font-serif-d text-[22px] text-[#0D1B2A]">{s.t}</div>
                <p className="mt-2 text-[16px] text-[#5C6470] leading-[1.7] max-w-sm">{s.d}</p>
                {i < STEPS.length - 1 && (
                  <div className="hidden md:block mt-8 h-px w-full bg-[#E5E7EA]" />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 6 — WHY THIS WHEN AI EXISTS */}
      <section className="bg-[#0D1B2A] text-white">
        <div className="max-w-[1240px] mx-auto px-5 sm:px-8 py-24 md:py-32">
          <div className="max-w-4xl reveal">
            <div className="text-[12px] uppercase tracking-[0.22em] text-[#F57F17] font-semibold">A fair question</div>
            <h2 className="mt-5 font-serif-d text-[36px] sm:text-5xl lg:text-[58px] leading-[1.06] text-white">
              If AI can answer anything, why teach this at all?
            </h2>
            <p className="mt-8 text-[17px] sm:text-[19px] text-white/85 leading-[1.75]">
              Because AI answers the questions you know to ask. It cannot tell you that a question exists. A teenager cannot search "what to do after scanning a fraud QR code" in the three seconds before the money is gone. They cannot Google a rule they have never heard of. LifeIQ builds the awareness that a danger or an option exists — and the trained reflex that acts before there is time to search. AI then makes a capable young person even sharper. The two work together. One is far weaker without the other.
            </p>
          </div>
        </div>
      </section>

      {/* SECTION 7 — CYBER SAFETY */}
      <section id="cyber" className="bg-[#F5F5F4]">
        <div className="max-w-[1240px] mx-auto px-5 sm:px-8 py-24 md:py-32">
          <div className="max-w-4xl reveal">
            <div className="flex items-center gap-3 text-[12px] uppercase tracking-[0.22em] text-[#F57F17] font-semibold">
              <span className="inline-block w-2 h-2 rounded-full bg-[#F57F17]" />
              Cyber Safety Module
            </div>
            <h2 className="mt-5 font-serif-d text-[36px] sm:text-5xl lg:text-[54px] leading-[1.08]">
              Even the most educated people are being cheated. Knowing a lot is not the same as being prepared.
            </h2>
          </div>

          <div className="mt-10 max-w-3xl reveal">
            <p className="text-[17px] sm:text-[19px] text-[#1A1A1A] leading-[1.75]">
              In 2025, a retired IAS Joint Director lost ₹45 lakh to a digital-arrest scam. A retired Army Colonel lost ₹56 lakh. An 82-year-old in Banjara Hills lost ₹72 lakh. These were not careless or uneducated people — the fraud exploits fear and authority, not ignorance. If it can reach them, it can reach anyone. LifeIQ's dedicated 16-chapter Cyber Safety module teaches the specific, practised reflexes that protect a whole family — digital arrest, fake government sites, UPI scams, AI voice cloning, SIM swap, and investment fraud — grounded in real cases from Hyderabad and Andhra Pradesh.
            </p>
            <a href="/cyber-safety" data-testid="cyber-cta" className="mt-8 inline-flex items-center gap-2 border border-[#F57F17] text-[#F57F17] px-7 py-3.5 text-[13px] font-semibold tracking-wide uppercase hover:bg-[#F57F17] hover:text-white transition rounded-sm">
              See the Cyber Safety Module <ArrowRight size={14} />
            </a>
          </div>
        </div>
      </section>

      {/* SECTION 8 — FOR SCHOOLS / FOR PARENTS */}
      <section className="bg-white">
        <div className="max-w-[1240px] mx-auto px-5 sm:px-8 py-24 md:py-32">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
            <article id="for-schools" data-testid="for-schools-card" className="reveal bg-white border border-[#E5E7EA] rounded-sm overflow-hidden flex flex-col">
              <div className="h-1.5 bg-[#004D40]" />
              <div className="p-8 sm:p-10 flex flex-col grow">
                <div className="text-[12px] uppercase tracking-[0.22em] text-[#004D40] font-semibold">For Schools</div>
                <h3 className="mt-4 font-serif-d text-[28px] sm:text-[32px] leading-tight">A meaningful new pillar for the school's identity.</h3>
                <p className="mt-5 text-[16px] text-[#1A1A1A] leading-[1.7]">
                  A genuinely differentiated, NEP 2020-aligned programme. One period a week. No special infrastructure. Complete teacher training and certification included. A measurable outcomes story for management and parents.
                </p>
                <a href="/for-schools" data-testid="schools-cta" className="mt-8 inline-flex items-center gap-2 bg-[#0D1B2A] text-white px-6 py-3.5 text-[13px] font-semibold tracking-wide uppercase hover:bg-[#004D40] transition rounded-sm w-fit">
                  Partner With Us <ArrowRight size={14} />
                </a>
                <button
                  type="button"
                  onClick={() => setBrochureOpen(true)}
                  data-testid="schools-brochure"
                  className="mt-3 inline-flex items-center gap-2 text-[#004D40] text-[13px] font-semibold tracking-wide uppercase hover:underline underline-offset-4 w-fit"
                >
                  <Download size={14} /> Download the brochure (PDF)
                </button>
              </div>
            </article>

            <article id="for-parents" data-testid="for-parents-card" className="reveal bg-white border border-[#E5E7EA] rounded-sm overflow-hidden flex flex-col" style={{ transitionDelay: "80ms" }}>
              <div className="h-1.5 bg-[#F57F17]" />
              <div className="p-8 sm:p-10 flex flex-col grow">
                <div className="text-[12px] uppercase tracking-[0.22em] text-[#F57F17] font-semibold">For Parents</div>
                <h3 className="mt-4 font-serif-d text-[28px] sm:text-[32px] leading-tight">A capable, grounded young adult — who quietly protects the home too.</h3>
                <p className="mt-5 text-[16px] text-[#1A1A1A] leading-[1.7]">
                  A child who can handle a crisis, sees through a scam, understands money, and is becoming a capable, grounded adult — and who brings that protection home to the whole family.
                </p>
                <a href="#contact" data-testid="parents-cta" className="mt-8 inline-flex items-center gap-2 border border-[#0D1B2A] text-[#0D1B2A] px-6 py-3.5 text-[13px] font-semibold tracking-wide uppercase hover:bg-[#0D1B2A] hover:text-white transition rounded-sm w-fit">
                  Bring LifeIQ to a School <ArrowRight size={14} />
                </a>
              </div>
            </article>
          </div>
        </div>
      </section>

      {/* SECTION 9 — FINAL CTA + CONTACT */}
      <section id="contact" className="bg-[#0D1B2A] text-white">
        <div className="max-w-[1240px] mx-auto px-5 sm:px-8 py-24 md:py-32">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
            <div className="md:col-span-5 reveal">
              <div className="text-[12px] uppercase tracking-[0.22em] text-[#F57F17] font-semibold">Talk to us</div>
              <h2 className="mt-5 font-serif-d text-[36px] sm:text-5xl lg:text-[54px] leading-[1.06] text-white">
                It starts with one honest conversation.
              </h2>
              <p className="mt-7 text-[17px] sm:text-[19px] text-white/80 leading-[1.7] max-w-md">
                We are looking for forward-thinking schools in Hyderabad to run a pilot — one class, one term, measured before and after. No commitment beyond a conversation.
              </p>
              <div className="mt-8 text-[14px] text-white/60">
                <Link to="/about" className="underline underline-offset-4 hover:text-white">Read the LifeIQ story →</Link>
              </div>
            </div>
            <div className="md:col-span-7 reveal">
              <ContactForm idPrefix="home-contact" />
            </div>
          </div>
        </div>
      </section>

      <BrochureDialog open={brochureOpen} onOpenChange={setBrochureOpen} doc="brochure" />
      <BrochureDialog open={guideOpen} onOpenChange={setGuideOpen} doc="guide" />
    </main>
  );
}
